import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { router, usePage } from '@inertiajs/react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CreditsInfo from '../../Components/CreditsInfo';
import './Editor.css';

const VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
// "(default)" is not fixed — it belongs to whichever model this script's edition
// renders on, so it is applied at render time from `defaultProvider`, not baked
// into the label here.
const PROVIDERS = [
    { value: 'gpt_image_2', label: 'GPT Image 2.5 Flare', cost: 2 },
    { value: 'nano_banana_2', label: 'Nano Banana 2.1 — 1K (best quality)', cost: 3 },
    { value: 'gemini_flash', label: 'Gemini Flash', cost: 2 },
    // imagen and leonardo removed 2026-10-07: live probe showed both failing on
    // account funding (AIML out of funds, Leonardo out of API tokens). They remain
    // in the server-side fallback tail, where they fail fast at $0.
];
const POLL_MS = 2500;

// Generation prerequisites (module scope: cards use them too).
const imgInputsOk = (s) => !!s.image?.text || (!!s.description && !!s.scene_number);
const audInputsOk = (s) => !!s.voice?.text;

// Media state for a section side ('image' | 'audio'), merging live poll data
// over the server-rendered props.
function mediaState(section, poll, kind) {
    const live = poll?.sectionsByUuid?.[section.uuid]?.[kind];
    const status = live ? live.status : section[kind === 'image' ? 'image_status' : 'audio_status'];
    const ready = live
        ? live.ready
        : kind === 'image'
            ? !!section.image?.temporary_url
            : !!section.voice?.temporary_url;
    const error = live ? live.error : section[kind === 'image' ? 'image_error' : 'audio_error'];
    if (status === 'queued' || status === 'running') return { state: 'generating', error: null };
    // An asset that already exists is usable for the build even if a LATER
    // regeneration failed — a failed re-attempt must not mask the existing image,
    // or it would block the build for a section that already has a picture.
    if (ready) return { state: 'ready', error: status === 'failed' ? error : null, staleFail: status === 'failed' };
    if (status === 'failed') return { state: 'failed', error };
    return { state: 'none', error: null };
}

// A provider content refusal (violence, gore, sexual content, a real person)
// vs a technical fault. Retrying a content refusal is pointless, so autopilot
// stops on it immediately instead of burning retries.
const isContentRefusal = (err) => !!err && /declin|content rule|content polic|won'?t (generate|create)|safety system|moderation|rejected as a result|refuse|not allowed|flagged|violat|nsfw/i.test(err);

// Did GenerateSectionImageJob already spend the one safe reframe server-side?
// It stamps this sentinel onto image_error. ⚠️ Keep in sync with the string in
// GenerateSectionImageJob::handle() — a mismatch silently restores the old
// double-reframe (and double-charge) behaviour.
const serverReframed = (err) => !!err && /auto-reframe also refused/i.test(err);

function Chip({ kind, state, needsWords }) {
    // "none + no inputs" is the user's to fix — make it scannable on cards.
    const effective = state === 'none' && needsWords ? 'needs' : state;
    const label = { ready: '✓', generating: '⟳', failed: '✕', none: '–', needs: '✎' }[effective];
    const title = effective === 'needs'
        ? `${kind}: needs your words first (${kind === 'image' ? 'description + scene' : 'voiceover text'})`
        : `${kind}: ${state}`;
    return (
        <span className={`ve-chip ${effective}`} title={title}>
            {label} {kind === 'image' ? 'img' : '♪'}
        </span>
    );
}

function SectionCard({ section, poll, selected, onSelect, sortable, num }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: section.id,
        disabled: !sortable,
    });
    const style = { transform: CSS.Transform.toString(transform), transition };
    const img = mediaState(section, poll, 'image');
    const aud = mediaState(section, poll, 'audio');
    const isPoster = section.order_number === 0;

    return (
        <div
            ref={setNodeRef}
            style={style}
            id={`ve-card-${section.id}`}
            {...attributes}
            {...(sortable ? listeners : {})}
            className={`ve-card ${selected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${sortable ? 'sortable' : ''}`}
            onClick={() => onSelect(section.id)}
        >
            <span className="ve-card-label">{isPoster ? 'POSTER' : num}</span>
            <div className="ve-card-thumb">
                {section.image?.temporary_url
                    ? <img src={section.image.temporary_url} alt="" />
                    : <span>{img.state === 'generating' ? 'generating…' : 'no image'}</span>}
            </div>
            <div className="ve-card-body">
                <div className="ve-card-text">{section.voice?.text || <em>no voiceover yet</em>}</div>
            </div>
            <div className="ve-chips">
                <Chip kind="image" state={img.state} needsWords={!imgInputsOk(section)} />
                <Chip kind="audio" state={aud.state} needsWords={!audInputsOk(section)} />
            </div>
        </div>
    );
}

export default function Editor() {
    const {
        video, sections: propSections, characters: propCharacters, settings,
        sceneMap, sceneTextMap, credits: initialCredits, movie_hash, movie_title,
        user_email, videoUrl, build: propBuild, videoOutdated: propVideoOutdated, flash, errors, edition,
    } = usePage().props;

    // Open the Model dropdown on whatever the pipeline would have used for this
    // script's edition: Deep Focus (deluxe) renders stills on Nano Banana 2,
    // Standard on GPT Image 2. Hardcoding gpt_image_2 meant every Deep Focus
    // section silently dropped to the Standard model as soon as anyone touched it.
    const defaultProvider = edition === 'deluxe' ? 'nano_banana_2' : 'gpt_image_2';

    const [credits, setCredits] = useState(initialCredits);
    const [selectedId, setSelectedId] = useState(null);
    const [poll, setPoll] = useState(null);
    const [pollErrors, setPollErrors] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [sceneModal, setSceneModal] = useState(null);
    const [watchOpen, setWatchOpen] = useState(false);
    const [busy, setBusy] = useState({});           // generic per-key busy flags
    const [charBusy, setCharBusy] = useState({});   // per-character regenerate
    const prevSectionIds = useRef(propSections.map((s) => s.id));
    const fingerprint = useRef('');
    // ── autopilot ("take it all the way") ──
    const [autopilot, setAutopilot] = useState(false);
    const [autoStop, setAutoStop] = useState(null);   // { title, detail, jump }
    const autoAttempts = useRef({});                   // { [sectionId]: {image,audio} } — autopilot retries only
    const castAttempts = useRef(0);
    const buildAttempts = useRef(0);
    const autoBusy = useRef(false);
    const autoLastSig = useRef(null);
    const autoLastProg = useRef(null);
    const autoStall = useRef(0);
    const saferTried = useRef({});    // sectionId -> a safe reframe was already tried
    const saferPending = useRef({});  // sectionId -> safe reframe in flight

    const sections = propSections; // server is source of truth; poll only drives status
    const characters = propCharacters;
    const overallLook = settings && settings.length > 0 ? settings[0] : null;

    const scenes = useMemo(() => sections.filter((s) => s.order_number !== 0), [sections]);
    const selected = sections.find((s) => s.id === selectedId) || scenes[0] || sections[0] || null;

    const missingCast = characters.filter((c) => !c.asset_id);
    const castReadyIds = new Set(characters.filter((c) => c.asset_id).map((c) => c.id));

    // ── flash + errors → toasts ──────────────────────────────────────────
    useEffect(() => {
        if (flash?.success) toast.success(flash.success);
        if (flash?.error) toast.error(flash.error);
    }, [flash]);
    useEffect(() => {
        if (errors) Object.values(errors).forEach((e) => toast.error(String(e)));
    }, [errors]);

    // Auto-select a freshly added section.
    useEffect(() => {
        const added = propSections.map((s) => s.id).filter((id) => !prevSectionIds.current.includes(id));
        if (added.length === 1) setSelectedId(added[0]);
        prevSectionIds.current = propSections.map((s) => s.id);
    }, [propSections]);

    // ── status polling ───────────────────────────────────────────────────
    const isActive = useCallback((data) => {
        if (!data) {
            // No poll yet: assume active if props suggest in-flight work.
            return (video.status || '').includes('progress')
                || sections.some((s) => ['queued', 'running'].includes(s.image_status) || ['queued', 'running'].includes(s.audio_status))
                || characters.some((c) => ['queued', 'running'].includes(c.image_status));
        }
        const buildActive = data.build && ['queued', 'running'].includes(data.build.status);
        const mediaActive = data.sections.some((s) =>
            ['queued', 'running'].includes(s.image.status) || ['queued', 'running'].includes(s.audio.status));
        const castActive = data.characters.some((c) => ['queued', 'running'].includes(c.status));
        // A fresh take has no sections yet — the video status string is the
        // only signal that creation is still running.
        const vs = (data.video?.status || '').toLowerCase();
        const creationActive = vs.includes('progress') || vs.includes('creating');
        return buildActive || mediaActive || castActive || creationActive;
    }, [video.status, sections, characters]);

    const [polling, setPolling] = useState(true);

    useEffect(() => {
        if (!polling) return undefined;
        let cancelled = false;
        const tick = async () => {
            try {
                const { data } = await axios.get(`/videos/${video.uuid}/status`);
                if (cancelled) return;
                setPollErrors(0);
                const byUuid = {};
                data.sections.forEach((s) => { byUuid[s.uuid] = s; });
                setPoll({ ...data, sectionsByUuid: byUuid });

                // When something finishes, refresh props (new temp URLs, build result).
                const fp = JSON.stringify([
                    data.build?.status,
                    data.sections.map((s) => [s.image.ready, s.image.status, s.audio.ready, s.audio.status]),
                    data.characters.map((c) => [c.ready, c.status]),
                ]);
                if (fingerprint.current && fingerprint.current !== fp) {
                    router.reload({ only: ['sections', 'characters', 'videoUrl', 'video', 'build', 'videoOutdated', 'settings'] });
                }
                fingerprint.current = fp;

                if (!isActive(data)) setPolling(false);
            } catch (e) {
                if (!cancelled) setPollErrors((n) => n + 1);
            }
        };
        tick();
        const timer = setInterval(tick, POLL_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, [polling, video.id, isActive]);

    const wake = () => { setPollErrors(0); setPolling(true); };

    // Announce the moment work finishes and the next step changes — a header
    // card flipping quietly doesn't grab someone focused on a section panel.
    const prevGuideKey = useRef(null);

    // Temp S3 URLs expire (~15 min). On an idle page nothing refreshes them,
    // so audio/images quietly die. Renew props on a slow heartbeat.
    useEffect(() => {
        const t = setInterval(() => {
            router.reload({ only: ['sections', 'characters', 'videoUrl'] });
        }, 10 * 60 * 1000);
        return () => clearInterval(t);
    }, []);

    const fetchCredits = useCallback(async () => {
        try {
            const { data } = await axios.get(`/credits/${movie_hash}`);
            setCredits(data.credits);
        } catch (e) { /* non-fatal */ }
    }, [movie_hash]);

    // ── derived build state ──────────────────────────────────────────────
    const build = poll?.build || propBuild;
    const buildRunning = build && ['queued', 'running'].includes(build.status);
    const buildFailed = build && build.status === 'failed';
    // From the top-level flag, not build.outdated: a video rendered outside the editor
    // has no build row but must still flip to "Rebuild" when its sections change.
    const buildOutdated = poll?.video ? !!poll.video.outdated : !!(propVideoOutdated ?? build?.outdated);
    const readyCount = sections.filter((s) =>
        mediaState(s, poll, 'image').state === 'ready' && mediaState(s, poll, 'audio').state === 'ready').length;
    const allReady = readyCount === sections.length && sections.length > 0;
    const missingCount = sections.length - readyCount;

    // ── actions ──────────────────────────────────────────────────────────
    const saveSectionField = (section, payload) => {
        // Strip null/empty keys: sending scene_number:null on a fresh
        // section used to fail validation and eat the user's edits.
        const clean = Object.fromEntries(
            Object.entries(payload).filter(([, v]) => v !== null && v !== undefined && v !== ''));
        axios.put(`/sections/${section.uuid}`, clean).catch(() => toast.error('Could not save changes.'));
    };

    const regenerateImage = async (section, opts = {}) => {
        const provider = opts.provider || defaultProvider;
        const cost = PROVIDERS.find((p) => p.value === provider)?.cost ?? 2;
        if (credits < cost) { toast.error(`Not enough credits (needs ${cost}).`); return; }
        setBusy((b) => ({ ...b, [`img${section.id}`]: true }));
        try {
            await axios.post(`/sections/${section.uuid}/regenerate-image`, {
                description: opts.description ?? section.description ?? '',
                scene_number: opts.scene_number ?? section.scene_number ?? null,
                character_ids: opts.character_ids ?? section.character_ids ?? [],
                provider,
                prompt: opts.prompt ?? null,
            });
            toast.info('Image generation started.');
            wake();
            fetchCredits();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not start image generation.');
        } finally {
            setBusy((b) => ({ ...b, [`img${section.id}`]: false }));
        }
    };

    const regenerateAudio = async (section, voiceText, voice) => {
        if (!voiceText) { toast.error('Add voiceover text first.'); return; }
        if (credits < 1) { toast.error('Not enough credits (needs 1).'); return; }
        setBusy((b) => ({ ...b, [`aud${section.id}`]: true }));
        try {
            await axios.post(`/sections/${section.uuid}/regenerate-audio`, { voice_text: voiceText, voice });
            toast.info('Audio generation started.');
            wake();
            fetchCredits();
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not start audio generation.');
        } finally {
            setBusy((b) => ({ ...b, [`aud${section.id}`]: false }));
        }
    };

    // Prompt-only generation: write the detailed prompt (free) so the user
    // can inspect/tweak it in Advanced before spending credits on the image.
    const writePromptOnly = async (section, description, sceneNumber) => {
        if (!description?.trim() || !sceneNumber) {
            toast.error('Write the instructions and pick a scene first.');
            return;
        }
        setBusy((b) => ({ ...b, [`prm${section.id}`]: true }));
        try {
            await axios.post(`/sections/${section.uuid}/create-image-prompt`, {
                description, scene_number: sceneNumber,
            });
            // The prompt may take ~10-60s; poll until it lands.
            const start = Date.now();
            const timer = setInterval(async () => {
                if (Date.now() - start > 90000) {
                    clearInterval(timer);
                    setBusy((b) => ({ ...b, [`prm${section.id}`]: false }));
                    toast.error('Prompt generation timed out — try again.');
                    return;
                }
                try {
                    const { data } = await axios.get(`/sections/${section.uuid}/prompt`);
                    if (data?.ready && data?.prompt) {
                        clearInterval(timer);
                        setDraft((d) => ({ ...d, prompt: data.prompt }));
                        setBusy((b) => ({ ...b, [`prm${section.id}`]: false }));
                        toast.success('Prompt ready — review and tweak it below.');
                    }
                } catch (e) { /* keep polling */ }
            }, 1500);
        } catch (e) {
            setBusy((b) => ({ ...b, [`prm${section.id}`]: false }));
            toast.error(e.response?.data?.message || 'Could not start prompt generation.');
        }
    };

    const addSection = (afterOrder) => {
        router.post(`/videos/${video.uuid}/sections`, { order_number: afterOrder }, { preserveScroll: true });
    };

    const deleteSection = (section) => {
        if (!confirm('Delete this section?')) return;
        router.delete(`/sections/${section.uuid}`, { preserveScroll: true });
    };

    // Content refusal remedy: ask the model to reframe the prompt SFW (same beat,
    // a different moment or framing) then regenerate with standard retries.
    const saferImageRegenerate = async (section) => {
        // The model the user chose for this section, not the edition default — picks
        // are strict (2026-10-08), so the safer version must go to the SAME model.
        const provider = (selectedId === section.id && draft.provider) ? draft.provider : defaultProvider;
        const cost = PROVIDERS.find((p) => p.value === provider)?.cost ?? 2;
        if (credits < cost) { toast.error(`Not enough credits (needs ${cost}).`); return false; }
        const desc = (section.description || '').trim();
        const scene = section.scene_number || null;
        if (!desc || !scene) { toast.error('This section needs a description and a scene first.'); return false; }
        const before = (section.image?.text || '').trim();
        setBusy((b) => ({ ...b, [`safe${section.id}`]: true }));
        try {
            await axios.post(`/sections/${section.uuid}/create-image-prompt`, { description: desc, scene_number: scene, safe: 1 });
            const start = Date.now();
            const newPrompt = await new Promise((resolve, reject) => {
                const t = setInterval(async () => {
                    if (Date.now() - start > 90000) { clearInterval(t); reject(new Error('unchanged')); return; }
                    try {
                        const { data } = await axios.get(`/sections/${section.uuid}/prompt`);
                        if (data?.ready && data?.prompt && data.prompt.trim() !== before) { clearInterval(t); resolve(data.prompt); }
                    } catch (err) { /* keep polling */ }
                }, 3000);
            });
            if (selectedId === section.id) setDraft((d) => ({ ...d, prompt: newPrompt }));
            await regenerateImage(section, { prompt: newPrompt, provider });
            toast.info(`Section ${section.order_number === 0 ? '(poster)' : section.order_number}: safer prompt created — regenerating.`);
            return true;
        } catch (e) {
            toast.error('Could not create a safer version — this moment may be too graphic to reframe automatically. Edit the description to be less explicit, then try again.');
            return false;
        } finally {
            setBusy((b) => ({ ...b, [`safe${section.id}`]: false }));
        }
    };

    const buildVideo = () => {
        router.post(`/videos/${video.uuid}/build`, {}, {
            preserveScroll: true,
            onSuccess: () => wake(),
        });
    };

    const generateMissing = () => {
        router.post(`/videos/${video.uuid}/generateMedia`, {
            auto_generate_characters: missingCast.length > 0 ? 'true' : 'false',
        }, { preserveScroll: true, onSuccess: () => wake() });
    };

    const generateCast = () => {
        router.post(`/videos/${video.uuid}/generate-cast`, {}, {
            preserveScroll: true, onSuccess: () => wake(),
        });
    };

    const startAutopilot = () => {
        autoAttempts.current = {};
        castAttempts.current = 0;
        buildAttempts.current = 0;
        autoBusy.current = false;
        autoLastSig.current = null;
        autoLastProg.current = null;
        autoStall.current = 0;
        saferTried.current = {};
        saferPending.current = {};
        setAutoStop(null);
        setAutopilot(true);
    };

    // Warn before leaving while autopilot is chaining steps.
    useEffect(() => {
        if (!autopilot) return undefined;
        const onLeave = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', onLeave);
        return () => window.removeEventListener('beforeunload', onLeave);
    }, [autopilot]);

    // Autopilot: on each settled poll tick, do the next big thing —
    // cast -> sections -> retry failures (cap 2) -> build. Stops with a
    // reason the moment it hits something it cannot fix.
    useEffect(() => {
        if (!autopilot || autoBusy.current || pollErrors >= 3) return;

        const imgSt = (s) => mediaState(s, poll, 'image');
        const audSt = (s) => mediaState(s, poll, 'audio');
        // Cast generates on the character (description) rows, not sections, so it
        // must be checked here too — otherwise autopilot re-fires generateCast while
        // the first pass is still running and burns the retry cap before the
        // references land (false "cast could not be generated").
        const castRows = poll?.characters || null;
        const castGen = castRows ? castRows.some((c) => ['queued', 'running'].includes(c.status)) : false;
        if (castGen || buildRunning || sections.some((s) => imgSt(s).state === 'generating' || audSt(s).state === 'generating')) return;

        const missCast = castRows ? castRows.filter((c) => !c.ready) : characters.filter((c) => !c.asset_id);
        const failed = sections.filter((s) => imgSt(s).state === 'failed' || audSt(s).state === 'failed');
        const genable = sections.filter((s) => (imgSt(s).state !== 'ready' && imgInputsOk(s)) || (audSt(s).state !== 'ready' && audInputsOk(s)));
        const needInput = sections.filter((s) => (imgSt(s).state !== 'ready' && !imgInputsOk(s)) || (audSt(s).state !== 'ready' && !audInputsOk(s)));
        const readyN = sections.filter((s) => imgSt(s).state === 'ready' && audSt(s).state === 'ready').length;
        const allRdy = sections.length > 0 && readyN === sections.length;
        const cost = missCast.length + sections.reduce((n, s) => n
            + (imgSt(s).state !== 'ready' && imgInputsOk(s) ? 2 : 0)
            + (audSt(s).state !== 'ready' && audInputsOk(s) ? 1 : 0), 0);

        const halt = (title, detail, jump) => { setAutopilot(false); setAutoStop({ title, detail, jump: jump || null }); };
        const act = (sig, run) => {
            const prog = `${readyN}|${missCast.length}|${failed.length}`;
            autoStall.current = (sig === autoLastSig.current && prog === autoLastProg.current) ? autoStall.current + 1 : 0;
            if (autoStall.current >= 2) {
                halt('Autopilot stalled', "The last step didn't make progress after a couple of tries. Check the section panel for an error, or carry on manually.");
                return;
            }
            autoLastSig.current = sig;
            autoLastProg.current = prog;
            autoBusy.current = true;
            run();
            window.setTimeout(() => { autoBusy.current = false; }, 8000);
        };

        if (cost > credits && (missCast.length || genable.length)) {
            halt('Not enough credits', `The remaining steps need about ${cost} credits and you have ${credits}. Add credits, then press "Take it all the way" again.`);
            return;
        }
        if (missCast.length > 0) {
            if (castAttempts.current >= 2) {
                halt(`${missCast.length} cast reference${missCast.length > 1 ? 's' : ''} could not be generated`,
                    'Reference images failed twice. Open Cast to see the error — usually a name or description the image provider rejected. Sections that use these characters cannot be generated until a reference exists.');
                return;
            }
            act('cast', () => { castAttempts.current += 1; generateCast(); });
            return;
        }
        // Image content refusals: try ONE safe reframe of the prompt before
        // giving up. If a reframed prompt is ALSO refused, stop and hand over.
        const imgRefused = failed.filter((s) => imgSt(s).state === 'failed' && isContentRefusal(imgSt(s).error));
        // GenerateSectionImageJob now spends the one safe reframe server-side and
        // stamps the error when it did. Reframing again here would overwrite the
        // safe prompt it just wrote, burn another provider chain, and charge the
        // user a second time for a single refusal — so treat those as stuck.
        const freshRefused = imgRefused.filter((s) => !saferTried.current[s.id] && !serverReframed(imgSt(s).error));
        if (freshRefused.length > 0) {
            const s = freshRefused[0];
            saferTried.current[s.id] = true;
            saferPending.current[s.id] = true;
            autoBusy.current = true;
            saferImageRegenerate(s).finally(() => {
                window.setTimeout(() => { delete saferPending.current[s.id]; autoBusy.current = false; }, 6000);
            });
            return;
        }
        const refusedStuck = imgRefused.filter((s) => saferTried.current[s.id] || serverReframed(imgSt(s).error));
        if (refusedStuck.length > 0) {
            const s = refusedStuck[0];
            const label = s.order_number === 0 ? '(poster)' : s.order_number;
            halt(`Section ${label} — couldn't make a safe image`,
                `Even a reframed, safer prompt was refused for this moment. Edit this section's "what this image shows" to something less graphic, then run it again. (The refused prompts were emailed for review.)`,
                s);
            return;
        }
        // Remaining failures (technical images, or audio): retry to the cap, then stop.
        const stopNow = failed.filter((s) => {
            const a = autoAttempts.current[s.id] || {};
            return (imgSt(s).state === 'failed' && (a.image || 0) >= 2) || (audSt(s).state === 'failed' && (a.audio || 0) >= 2);
        });
        if (stopNow.length > 0) {
            const s = stopNow[0];
            const label = s.order_number === 0 ? '(poster)' : s.order_number;
            const err = imgSt(s).state === 'failed' ? imgSt(s).error : audSt(s).error;
            halt(`Section ${label} failed`,
                `${err || 'Generation failed.'}\n\nWe tried a couple of times — this looks like a temporary provider issue rather than your prompt. You can retry, and we're looking into it.`,
                s);
            return;
        }
        if (failed.length > 0) {
            act('retry', () => {
                failed.forEach((s) => {
                    const a = autoAttempts.current[s.id] || { image: 0, audio: 0 };
                    if (imgSt(s).state === 'failed') a.image = (a.image || 0) + 1;
                    if (audSt(s).state === 'failed') a.audio = (a.audio || 0) + 1;
                    autoAttempts.current[s.id] = a;
                });
                generateMissing();
            });
            return;
        }
        if (genable.length > 0) { act('gen', generateMissing); return; }
        if (needInput.length > 0) {
            halt(`${needInput.length} section${needInput.length > 1 ? 's' : ''} need your words`,
                'These have no description and scene (for the image) or no voiceover text (for the audio), so nothing can be generated. Fill them in or delete them, then press "Take it all the way" again.',
                needInput[0]);
            return;
        }
        if (allRdy && (!videoUrl || buildOutdated)) {
            if (buildFailed && buildAttempts.current >= 2) {
                halt('The build failed', (build?.error || 'No error recorded.').slice(0, 300));
                return;
            }
            act('build', () => { buildAttempts.current += 1; buildVideo(); });
            return;
        }
        if (videoUrl && allRdy && !buildOutdated) {
            setAutopilot(false);
            toast.success('Autopilot finished — your video is built.');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autopilot, poll, sections, characters, buildRunning, buildFailed, buildOutdated, videoUrl, credits, pollErrors]);

    const setPrimary = () => {
        router.post(`/videos/${video.uuid}/primary`, {}, { preserveScroll: true });
    };

    const regenerateCharacter = async (character) => {
        if (credits < 1) { toast.error('Not enough credits (needs 1).'); return; }

        // Consistency warning: sections were generated against the current
        // reference — a new one WILL look different from those images.
        const usedWithImages = sections.filter((s) =>
            (s.character_ids || []).includes(character.id) && s.image?.temporary_url);
        if (character.asset_id && usedWithImages.length > 0) {
            const ok = confirm(
                `${character.name} already appears in ${usedWithImages.length} generated image${usedWithImages.length > 1 ? 's' : ''}. ` +
                `A new reference will look DIFFERENT from those — you'll want to regenerate those sections afterward to keep the video consistent ` +
                `(I'll offer that next). Continue?`
            );
            if (!ok) return;
        }

        setCharBusy((b) => ({ ...b, [character.id]: true }));
        try {
            await axios.post('/generate-character-image', { videoDescriptionId: character.id });
            toast.success(`${character.name}: reference image updated.`);
            fetchCredits();
            const usedIn = sections.filter((s) => (s.character_ids || []).includes(character.id));
            router.reload({ only: ['characters', 'sections'] });
            if (usedIn.length > 0 && confirm(
                `${usedIn.length} section${usedIn.length > 1 ? 's' : ''} use ${character.name}. ` +
                `Regenerate their images too? (${usedIn.length} credit${usedIn.length > 1 ? 's' : ''})`
            )) {
                for (const s of usedIn) {
                    // eslint-disable-next-line no-await-in-loop
                    await regenerateImage(s);
                }
            }
        } catch (e) {
            toast.error(e.response?.data?.error || 'Could not generate the reference image.');
        } finally {
            setCharBusy((b) => ({ ...b, [character.id]: false }));
        }
    };

    const saveDescription = async (descriptionRow, text) => {
        try {
            await axios.put(`/descriptions/${descriptionRow.id}`, { description: text });
            toast.success('Saved.');
        } catch (e) {
            toast.error('Could not save the description.');
        }
    };

    // ── drag reorder ─────────────────────────────────────────────────────
    // Mouse: small drag threshold. Touch: LONG-PRESS to drag — otherwise
    // dragging hijacks the filmstrip's horizontal swipe-scroll on phones.
    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    );
    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const ids = scenes.map((s) => s.id);
        const reordered = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
        axios.post(`/videos/${video.uuid}/sections/reorder`, { order: reordered })
            .then(() => router.reload({ only: ['sections'] }))
            .catch(() => toast.error('Could not reorder sections.'));
    };

    // Cast suggestion: when a section has no explicit tags in the DB
    // (videos created before convert_bps synced the pivot, or new blank
    // sections), pre-select cast members whose names appear in the
    // selected scene's text.
    const suggestCastForScene = useCallback((sceneNumber) => {
        const text = (sceneTextMap[sceneNumber] || '').toLowerCase();
        if (!text) return [];
        return characters
            .filter((c) => c.name && text.includes(c.name.toLowerCase()))
            .map((c) => c.id);
    }, [characters, sceneTextMap]);

    // ── selected-section local fields (autosave on blur) ─────────────────
    const [draft, setDraft] = useState({});
    useEffect(() => {
        if (!selected) return;
        const dbTags = selected.character_ids || [];
        const suggested = dbTags.length === 0 ? suggestCastForScene(selected.scene_number) : [];
        setDraft({
            description: selected.description || '',
            voiceText: selected.voice?.text || '',
            voice: selected.voice_name || 'alloy',
            prompt: selected.image?.text || '',
            provider: defaultProvider,
            scene: selected.scene_number || '',
            tags: dbTags.length > 0 ? dbTags : suggested,
            tagsSuggested: dbTags.length === 0 && suggested.length > 0,
        });
    }, [selectedId, selected?.id]);

    // Show the status screen (spinner or failure) whenever there's nothing to
    // edit yet: no sections at all, OR creation is still running / has failed
    // before any SCENE sections were written. The poster section is created
    // before the story step can fail, which made `selected` truthy and skipped
    // this block — dropping the user into an empty editor with no error. That
    // was the "says creating, then blank" bug.
    const creationFailed = (video.status || '').startsWith('Video creation failed');
    const creationInProgress = /creating|in progress/i.test(video.status || '');
    const noSceneSections = scenes.length === 0;
    if (!selected || (noSceneSections && (creationFailed || creationInProgress))) {
        const failed = creationFailed;
        // Uploaded videos are built but have no sections — nothing to edit.
        if (!failed && videoUrl) {
            return (
                <div className="container mt-5 text-center" style={{ maxWidth: 560 }}>
                    <h4>This is an uploaded video</h4>
                    <p className="text-muted">It has no editable sections — you can watch it, download it, or use it as the main video.</p>
                    <div className="d-flex gap-2 justify-content-center">
                        <a className="btn btn-primary" href={videoUrl} target="_blank" rel="noopener noreferrer">▶ Watch</a>
                        <a className="btn btn-outline-secondary" href={`/videos/${video.uuid}/download`} target="_blank" rel="noopener noreferrer">Download</a>
                        <button className="btn btn-outline-secondary" onClick={setPrimary}>Use as main video</button>
                        <a href={`/videos/list/${movie_hash}`} className="btn btn-outline-secondary">← Videos</a>
                    </div>
                </div>
            );
        }
        return (
            <div className="container mt-5 text-center" style={{ maxWidth: 560 }}>
                {failed ? (
                    <>
                        <h4 className="text-danger">Something went wrong creating this video</h4>
                        <p className="text-muted">{video.status}</p>
                        <a href={`/videos/list/${movie_hash}`} className="btn btn-outline-secondary">← Back to videos</a>
                    </>
                ) : (
                    <>
                        <div className="spinner-border text-primary mb-3" role="status" />
                        <h4>Creating your video…</h4>
                        {/* Live phase from the job's status updates. */}
                        <p className="fw-semibold" style={{ color: '#14536e' }}>
                            {(poll?.video?.status || video.status || '').replace(/^Creating — /, '') || 'Starting…'}
                        </p>
                        <p className="text-muted">
                            Reading the whole script takes a few minutes — sections will appear here
                            the moment they exist, then images and audio fill in one by one.
                            This page updates automatically; leaving and coming back is fine.
                        </p>
                    </>
                )}
            </div>
        );
    }

    const selImg = mediaState(selected, poll, 'image');
    const selAud = mediaState(selected, poll, 'audio');
    // Minutes since the selected section last made progress — powers the
    // "taking long? retry" escape hatch on stuck generations.
    const lastTouch = poll?.sectionsByUuid?.[selected.uuid]?.updated_at || selected.updated_at;
    const stuckMinutes = lastTouch ? (Date.now() - new Date(lastTouch).getTime()) / 60000 : 0;
    const selectedTagged = draft.tags || selected.character_ids || [];
    const taggedMissingRefs = selectedTagged.filter((id) => !castReadyIds.has(id));
    const imageGateBlocked = taggedMissingRefs.length > 0;
    const isPosterSelected = selected.order_number === 0;

    const usedInCount = (charId) =>
        sections.filter((s) => (s.character_ids || []).includes(charId)).length;

    const buildDisabledReason = buildRunning
        ? `Building… ${build?.step ? `(${build.step})` : ''}`
        : !allReady ? `${missingCount} section${missingCount > 1 ? 's' : ''} missing image or audio` : null;

    const anyGenerating = sections.some((s) =>
        mediaState(s, poll, 'image').state === 'generating' || mediaState(s, poll, 'audio').state === 'generating');

    // Missing media splits in two: generatable now (inputs exist) vs waiting
    // on the user's words (no voiceover text / no description+scene).
    const generatableCount = sections.filter((s) =>
        (mediaState(s, poll, 'image').state !== 'ready' && imgInputsOk(s))
        || (mediaState(s, poll, 'audio').state !== 'ready' && audInputsOk(s))).length;
    const needsInputSections = sections.filter((s) =>
        (mediaState(s, poll, 'image').state !== 'ready' && !imgInputsOk(s))
        || (mediaState(s, poll, 'audio').state !== 'ready' && !audInputsOk(s)));

    const jumpToNeedsInput = () => {
        const target = needsInputSections[0];
        if (target) {
            setSelectedId(target.id);
            document.getElementById(`ve-card-${target.id}`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
    };

    // ── "Clippy done right": one state-derived suggestion, never guessed.
    // Explains the real status, what it means, and carries the next action.
    const failedSections = sections.filter((s) =>
        mediaState(s, poll, 'image').state === 'failed' || mediaState(s, poll, 'audio').state === 'failed');
    const piecesImg = sections.filter((s) => mediaState(s, poll, 'image').state !== 'ready' && imgInputsOk(s)).length;
    const piecesAud = sections.filter((s) => mediaState(s, poll, 'audio').state !== 'ready' && audInputsOk(s)).length;
    const jumpTo = (target) => {
        if (!target) return;
        setSelectedId(target.id);
        document.getElementById(`ve-card-${target.id}`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };

    const guide = (() => {
        if (missingCast.length > 0) return {
            icon: '🎭',
            title: `Start with your cast — ${missingCast.length} character${missingCast.length > 1 ? 's' : ''} need reference images`,
            body: 'A reference image is what keeps a character looking like the same person in every section. Until a character has one, section images that include them are locked.',
            actionLabel: `Generate all ${missingCast.length} cast image${missingCast.length > 1 ? 's' : ''}`,
            onAction: generateCast,
        };
        if (anyGenerating) return {
            icon: '⏳',
            title: 'Generating — nothing to do but wait (or keep tweaking)',
            body: 'Images and audio are being created in the background, about a minute per piece. This page updates by itself; you can edit other sections meanwhile or even leave.',
        };
        if (failedSections.length > 0) return {
            icon: '⚠️',
            title: `${failedSections.length} piece${failedSections.length > 1 ? 's' : ''} failed to generate`,
            body: 'Failures happen — providers time out or reject a prompt. Each failed item shows its reason in the section panel, and a retry usually works.',
            actionLabel: 'Show me the first one',
            onAction: () => jumpTo(failedSections[0]),
        };
        if (needsInputSections.length > 0) return {
            icon: '✎',
            title: `${needsInputSections.length} section${needsInputSections.length > 1 ? 's' : ''} are waiting for your words`,
            body: 'An image needs a short description of what you picture plus the scene it belongs to; audio needs the voiceover text. Nothing can be generated for these until you write that — or delete the section if it was a mistake.',
            actionLabel: 'Take me there',
            onAction: jumpToNeedsInput,
        };
        if (!allReady && generatableCount > 0) return {
            icon: '⚙️',
            title: `${generatableCount} section${generatableCount > 1 ? 's' : ''} ready to generate`,
            body: `Everything needed is in place. One click generates the remaining images and audio (≈${piecesImg * 2 + piecesAud} credits, each piece charged only if it succeeds).`,
            actionLabel: `Generate ${generatableCount} missing`,
            onAction: generateMissing,
        };
        if (buildRunning) return {
            icon: '🎬',
            title: `Building your video${build?.step ? ` — ${build.step}` : ''}`,
            body: 'Rendering all sections into one file takes a couple of minutes. It appears here when done; leaving the page is fine.',
        };
        if (buildFailed) return {
            icon: '⚠️',
            title: `The build failed${build?.step ? ` at ${build.step}` : ''}`,
            body: (build?.error || 'No error recorded.').slice(0, 200),
            actionLabel: 'Retry build',
            onAction: buildVideo,
        };
        if (allReady && !videoUrl) return {
            key: 'build',
            icon: '🎬',
            title: 'Every section is ready — build your video',
            body: 'Build renders all sections, in order, into one video file you can watch, download and share.',
            actionLabel: 'Build video',
            onAction: buildVideo,
        };
        if (videoUrl && allReady && buildOutdated) return {
            key: 'rebuild',
            icon: '♻️',
            title: 'Your sections changed since the last build — rebuild to update the video',
            body: "Everything is generated, but the current video file is an older cut: it doesn't include your latest changes. Rebuilding is free and takes a couple of minutes.",
            actionLabel: 'Rebuild video',
            onAction: buildVideo,
        };
        if (videoUrl && allReady) return {
            key: 'ready',
            icon: '✓',
            title: 'Your video is ready',
            body: "Watch it, download it, or make it the main video shown with your script's analysis. Tweak any section and rebuild whenever you like.",
            actionLabel: '▶ Watch',
            onAction: () => setWatchOpen(true),
        };
        return null;
    })();

    // Autopilot render helpers.
    const videoComplete = !!videoUrl && allReady && !buildOutdated;
    const autoCost = missingCast.length + sections.reduce((n, s) => n
        + (mediaState(s, poll, 'image').state !== 'ready' && imgInputsOk(s) ? 2 : 0)
        + (mediaState(s, poll, 'audio').state !== 'ready' && audInputsOk(s) ? 1 : 0), 0);
    const autoPhase = missingCast.length ? 'generating cast'
        : failedSections.length ? 'retrying failed pieces'
        : generatableCount ? 'generating sections'
        : (allReady && !videoComplete) ? 'building your video'
        : 'working';

    // Toast when generation work concludes into an actionable state.
    useEffect(() => {
        const key = guide?.key || null;
        if (prevGuideKey.current && prevGuideKey.current !== key && ['rebuild', 'build', 'ready'].includes(key)) {
            toast.info(guide.title, { autoClose: 6000 });
        }
        prevGuideKey.current = key;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guide?.key]);

    return (
        <div>
            <ToastContainer position="bottom-right" />

            {pollErrors >= 3 && (
                <div className="ve-conn-lost">
                    Connection lost — status may be stale.{' '}
                    <button className="btn btn-sm btn-link p-0" onClick={wake}>Retry</button>
                </div>
            )}
            {/* (single guidance surface: the ve-guide card in the header) */}

            {/* ── Header ───────────────────────────────────────────── */}
            <div className="ve-header">
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <a href={`/videos/list/${movie_hash}`} className="btn btn-sm btn-outline-secondary">← Videos</a>
                    <a href={`/view_analysis?movieid=${movie_hash}`} target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary" title="Open this script's full analysis">
                        Analysis ↗
                    </a>
                    <span className="ve-title-wrap" title="Click to rename">
                        <input
                            className="ve-title-input"
                            defaultValue={video.title}
                            onBlur={(e) => {
                                if (e.target.value !== video.title) {
                                    router.put(`/videos/${video.uuid}`, { title: e.target.value }, { preserveScroll: true });
                                }
                            }}
                        />
                        <span className="ve-title-pencil" aria-hidden="true">✎</span>
                    </span>
                    <span className="small text-muted d-none d-md-inline">
                        Made from “{movie_title}” — click any frame below to change it
                    </span>
                    <button
                        className={`ve-cast-btn ${missingCast.length ? 'warn' : ''}`}
                        onClick={() => setDrawerOpen(true)}
                        title="Open the cast — reference images that keep characters consistent"
                    >
                        <span className="ve-avatars">
                            {characters.slice(0, 4).map((c) => (
                                c.tempUrl
                                    ? <img key={c.id} src={c.tempUrl} alt={c.name} className="ve-avatar" />
                                    : <span key={c.id} className="ve-avatar ve-avatar-empty">{(c.name || '?')[0]}</span>
                            ))}
                        </span>
                        <span className="ve-cast-btn-label">
                            Cast
                            {missingCast.length > 0
                                ? <small className="d-block text-danger">{missingCast.length} need images</small>
                                : <small className="d-block text-muted">{characters.length} characters</small>}
                        </span>
                    </button>
                    <span className="ms-auto badge text-bg-light border">⛁ {credits} credits</span>
                </div>

                {/* ── Quiet progress line: status only, no controls. The guide
                       below is the ONE element that talks and acts. ── */}
                <div className="ve-progressline mt-2">
                    <span className={`ve-pstep ${missingCast.length === 0 ? 'done' : 'active'}`}>
                        {missingCast.length === 0 ? '✓' : '①'} Cast {characters.length - missingCast.length}/{characters.length}
                    </span>
                    <span className="ve-step-arrow">·</span>
                    <span className={`ve-pstep ${allReady ? 'done' : missingCast.length === 0 ? 'active' : ''}`}>
                        {allReady ? '✓' : '②'} Sections {readyCount}/{sections.length}
                    </span>
                    <span className="ve-step-arrow">·</span>
                    <span className={`ve-pstep ${videoUrl && allReady && !buildFailed && !buildRunning && !buildOutdated ? 'done' : allReady ? 'active' : ''}`}>
                        {videoUrl && allReady && !buildFailed && !buildRunning && !buildOutdated ? '✓' : '③'} Build{' '}
                        {buildRunning ? 'running' : buildFailed ? 'failed'
                            : videoUrl ? ((allReady && !buildOutdated) ? 'done' : 'outdated') : '—'}
                    </span>
                </div>

                {/* ── THE one action band: state message + primary action +
                       quiet secondary links. Nothing else in the header acts. ── */}
                {guide && (
                    <div className="ve-guide mt-2">
                        <span className="ve-guide-icon" aria-hidden="true">{guide.icon}</span>
                        <span className="ve-guide-body">
                            <strong>{guide.title}</strong>
                            <span className="d-block">{guide.body}</span>
                        </span>
                        <span className="ve-guide-actions">
                            {guide.actionLabel && (
                                <button className="btn btn-primary" onClick={guide.onAction}>
                                    {guide.actionLabel}
                                </button>
                            )}
                            {videoUrl && !buildRunning && (
                                <span className="ve-guide-secondary">
                                    {guide.actionLabel !== '▶ Watch' && (
                                        <button className="btn btn-link btn-sm p-0" onClick={() => setWatchOpen(true)}>
                                            ▶ Watch{buildOutdated ? ' (old cut)' : ''}
                                        </button>
                                    )}
                                    <a className="btn btn-link btn-sm p-0" href={`/videos/${video.uuid}/download`} target="_blank" rel="noopener noreferrer">Download</a>
                                    <button className="btn btn-link btn-sm p-0" onClick={setPrimary}
                                        title="The main video is the one shown on your script's analysis page">
                                        Use as main
                                    </button>
                                    {!buildOutdated && allReady && (
                                        <button className="btn btn-link btn-sm p-0" disabled={!!buildDisabledReason}
                                            title={buildDisabledReason || 'Re-render with the current sections'} onClick={buildVideo}>
                                            Rebuild
                                        </button>
                                    )}
                                </span>
                            )}
                        </span>
                    </div>
                )}
                {/* Autopilot: one button to finish everything */}
                {autopilot ? (
                    <div className="ve-guide mt-2" style={{ borderColor: '#0d6efd' }}>
                        <span className="ve-guide-icon" aria-hidden="true">▶▶</span>
                        <span className="ve-guide-body">
                            <strong>Autopilot running — {autoPhase}</strong>
                            <span className="d-block">
                                Generating and building everything that's left, retrying failures automatically.{' '}
                                <b>Keep this tab open</b> — closing it stops autopilot (work already started still finishes; press the button again to pick up where it left off).
                            </span>
                        </span>
                        <span className="ve-guide-actions">
                            <button className="btn btn-outline-danger" onClick={() => setAutopilot(false)}>Stop</button>
                        </span>
                    </div>
                ) : autoStop ? (
                    <div className="ve-guide mt-2" style={{ borderColor: '#dc3545' }}>
                        <span className="ve-guide-icon" aria-hidden="true">⚠️</span>
                        <span className="ve-guide-body">
                            <strong>Autopilot stopped — {autoStop.title}</strong>
                            <span className="d-block" style={{ whiteSpace: 'pre-line' }}>{autoStop.detail}</span>
                        </span>
                        <span className="ve-guide-actions">
                            {autoStop.jump && (
                                <button className="btn btn-primary" onClick={() => { jumpTo(autoStop.jump); setAutoStop(null); }}>Show me</button>
                            )}
                            <button className="btn btn-link btn-sm p-0" onClick={() => setAutoStop(null)}>Dismiss</button>
                        </span>
                    </div>
                ) : (!videoComplete && (
                    <div className="ve-autopilot-launch mt-2">
                        <button className="btn btn-primary" onClick={startAutopilot}>
                            ▶▶ Take it all the way{autoCost > 0 ? ` (~${autoCost} credits)` : ''}
                        </button>
                        <small className="text-muted ms-2">
                            Generates cast, images and audio, retries failures, then builds — keep this tab open while it runs.
                        </small>
                    </div>
                ))}
            </div>

            {/* ── Timeline ─────────────────────────────────────────── */}
            <div className="ve-timeline">
                {sections.filter((s) => s.order_number === 0).map((s) => (
                    <SectionCard key={s.id} section={s} poll={poll} selected={selected.id === s.id} onSelect={setSelectedId} sortable={false} />
                ))}
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={scenes.map((s) => s.id)} strategy={horizontalListSortingStrategy}>
                        {scenes.map((s, i) => (
                            <React.Fragment key={s.id}>
                                <div className="ve-insert">
                                    <button title="Insert section here" onClick={() => addSection(s.order_number - 1)}>+</button>
                                </div>
                                <SectionCard section={s} poll={poll} selected={selected.id === s.id} onSelect={setSelectedId} sortable num={i + 1} />
                            </React.Fragment>
                        ))}
                    </SortableContext>
                </DndContext>
                <div className="ve-insert">
                    <button title="Add section at the end" onClick={() => addSection(scenes.length ? scenes[scenes.length - 1].order_number : 1)}>+</button>
                </div>
            </div>
            <div className="ve-timeline-help small text-muted">
                Click a frame to edit it below · drag to reorder · + inserts a new section · ✎ = needs your words
            </div>

            {/* ── Selected-section panel ───────────────────────────── */}
            <div className="container-fluid py-3">
                <div className="d-flex align-items-center mb-2">
                    <h5 className="mb-0">
                        {isPosterSelected ? 'Poster' : `Section ${scenes.findIndex((s) => s.id === selected.id) + 1} of ${scenes.length}`}
                    </h5>
                    {!isPosterSelected && (
                        <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => deleteSection(selected)}>Delete section</button>
                    )}
                </div>

                <div className="row g-3">
                    {/* IMAGE */}
                    <div className="col-lg-6">
                        <div className="card h-100">
                            <div className="card-header d-flex align-items-center">
                                Image
                                <span className={`ve-chip ms-2 ${selImg.state}`}>{selImg.state}</span>
                                {selImg.state === 'failed' && selected.image_error && (
                                    <span className="text-danger small ms-2 text-truncate" title={selected.image_error}>{selected.image_error}</span>
                                )}
                                {selImg.state === 'generating' && stuckMinutes > 2 && (
                                    <button className="btn btn-link btn-sm p-0 ms-2 text-warning-emphasis"
                                        onClick={() => regenerateImage(selected, { prompt: draft.prompt || null, description: draft.description, provider: draft.provider, scene_number: draft.scene || null, character_ids: draft.tags || [] })}>
                                        Taking long? Cancel & retry
                                    </button>
                                )}
                            </div>
                            <div className="card-body">
                                {selected.image?.temporary_url
                                    ? (
                                        <>
                                            <img src={selected.image.temporary_url} alt="" className="img-fluid rounded mb-1" style={{ maxHeight: 280 }} />
                                            {selected.image.generation_provider && (
                                                <div className="text-muted small mb-2">
                                                    {selected.image.generation_provider === 'upload'
                                                        ? 'uploaded by you'
                                                        : `made with ${PROVIDERS.find((p) => p.value === selected.image.generation_provider)?.label || selected.image.generation_provider}`}
                                                </div>
                                            )}
                                        </>
                                    )
                                    : <p className="text-muted">No image yet.</p>}

                                {/* Scene = the grounding for the generated prompt (full scene
                                    text is pulled by number). First-class, not Advanced. */}
                                {!isPosterSelected && (
                                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                                        <label className="form-label mb-0">Scene</label>
                                        <select
                                            className="form-select form-select-sm w-auto"
                                            value={draft.scene}
                                            onChange={(e) => {
                                                const newScene = e.target.value;
                                                // Characters present depend on the scene: existing tags
                                                // were chosen for the OLD scene, so a scene change always
                                                // re-derives them from the new scene's text. Chips stay
                                                // editable for corrections.
                                                const next = { ...draft, scene: newScene };
                                                const payload = { scene_number: newScene };
                                                if (newScene) {
                                                    next.tags = suggestCastForScene(newScene);
                                                    next.tagsSuggested = next.tags.length > 0;
                                                    payload.character_ids = next.tags;
                                                }
                                                setDraft(next);
                                                saveSectionField(selected, payload);
                                            }}
                                        >
                                            <option value="">—</option>
                                            {Object.entries(sceneMap).map(([num, title]) => (
                                                <option key={num} value={num}>{num} — {title}</option>
                                            ))}
                                        </select>
                                        {draft.scene && (
                                            <button className="btn btn-sm btn-link p-0" onClick={() => setSceneModal(draft.scene)}>view scene</button>
                                        )}
                                        {String(draft.scene) !== String(selected.scene_number || '') && selected.image?.temporary_url && (
                                            <span className="small text-primary">Scene changed — the image will be rebuilt for scene {draft.scene}.</span>
                                        )}
                                    </div>
                                )}

                                {/* ONE source of truth: the instructions. Editing them and
                                    regenerating is the whole model — no separate delta box. */}
                                <label className="form-label">
                                    {selected.image?.temporary_url
                                        ? <>Instructions — what this image shows <span className="text-muted small fw-normal ms-2">the short version — rebuild the full prompt below from it</span></>
                                        : 'What do you picture?'}
                                </label>
                                <textarea
                                    className="form-control mb-2"
                                    rows={3}
                                    value={draft.description}
                                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                                    onBlur={() => saveSectionField(selected, { description: draft.description })}
                                    placeholder="e.g. rain on the boathouse window, a silhouette slumped inside"
                                />

                                {characters.length > 0 && (
                                    <div className="mb-2">
                                        <label className="form-label d-block">
                                            In this image:
                                            {draft.tagsSuggested && (
                                                <span className="text-muted small ms-2">(suggested from scene {selected.scene_number})</span>
                                            )}
                                        </label>
                                        <div className="d-flex flex-wrap gap-1">
                                            {characters.map((c) => {
                                                const on = selectedTagged.includes(c.id);
                                                const noRef = !c.asset_id;
                                                return (
                                                    <button
                                                        key={c.id}
                                                        className={`ve-tag ${on ? 'on' : ''} ${noRef && on ? 'warn' : ''}`}
                                                        title={noRef ? `${c.name} has no reference image yet` : c.name}
                                                        onClick={() => {
                                                            const next = on
                                                                ? selectedTagged.filter((id) => id !== c.id)
                                                                : [...selectedTagged, c.id];
                                                            setDraft({ ...draft, tags: next, tagsSuggested: false });
                                                            saveSectionField(selected, { character_ids: next, scene_number: selected.scene_number });
                                                        }}
                                                    >
                                                        {on ? '◉' : '◯'} {c.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <label className="form-label mb-0">Model</label>
                                    <select
                                        className="form-select form-select-sm w-auto"
                                        value={draft.provider}
                                        onChange={(e) => setDraft({ ...draft, provider: e.target.value })}
                                    >
                                        {PROVIDERS.map((p) => (
                                            <option key={p.value} value={p.value}>
                                                {p.label}{p.value === defaultProvider ? ' (default)' : ''} — {p.cost} cr
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        className="btn btn-primary"
                                        disabled={busy[`img${selected.id}`] || selImg.state === 'generating' || imageGateBlocked
                                            || (!draft.prompt && !((draft.description || '').trim() && draft.scene))}
                                        title={imageGateBlocked ? 'Tagged characters need reference images first — open Cast'
                                            : (!draft.prompt && !((draft.description || '').trim() && draft.scene))
                                                ? 'Write the instructions and pick a scene first' : ''}
                                        onClick={() => regenerateImage(selected, { prompt: draft.prompt || null, description: draft.description, provider: draft.provider, scene_number: draft.scene || null, character_ids: draft.tags || [] })}
                                    >
                                        {selImg.state === 'generating' ? 'Generating…'
                                            : `${selected.image?.temporary_url ? 'Regenerate' : 'Generate'} image · ${PROVIDERS.find((p) => p.value === draft.provider)?.cost ?? 1} cr`}
                                    </button>
                                    <span className="text-muted small">or</span>
                                    <label className="btn btn-outline-secondary mb-0" title="Use your own image for this section — free">
                                        ⬆ Upload {selected.image?.temporary_url ? 'replacement' : 'your own'}
                                        <input type="file" accept="image/*" hidden onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            router.post(`/sections/${selected.uuid}/upload-image`, { image: file }, { preserveScroll: true, forceFormData: true });
                                        }} />
                                    </label>
                                </div>
                                {selImg.state === 'failed' && (
                                    <button
                                        className="btn btn-outline-danger mt-2"
                                        onClick={() => regenerateImage(selected, {
                                            // Retry = same prompt if one exists, else rebuild from description.
                                            prompt: selected.image?.text || null,
                                            description: draft.description,
                                            provider: draft.provider,
                                        })}
                                    >
                                        Retry
                                    </button>
                                )}
                                {isContentRefusal(selImg.error) && (
                                    <button
                                        className="btn btn-warning mt-2 ms-2"
                                        disabled={busy[`safe${selected.id}`]}
                                        title="Reframe this as a safe-for-generation image that still fits the story, then regenerate"
                                        onClick={() => saferImageRegenerate(selected)}
                                    >
                                        {busy[`safe${selected.id}`] ? 'Creating safer prompt…' : "🛟 Create 'safer' image prompt & regenerate"}
                                    </button>
                                )}
                                {imageGateBlocked && (
                                    <div className="form-text text-danger">
                                        Locked: generate references for {characters.filter((c) => taggedMissingRefs.includes(c.id)).map((c) => c.name).join(', ')} first.
                                    </div>
                                )}

                                <div className="mt-3">
                                    <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
                                        <label className="form-label mb-0">Full image prompt <span className="text-muted small fw-normal">— the detailed version sent to the image model</span></label>
                                        <button
                                            className="btn btn-sm btn-outline-primary ms-auto"
                                            disabled={busy[`prm${selected.id}`] || !(draft.description || '').trim() || !draft.scene}
                                            title={!(draft.description || '').trim() || !draft.scene
                                                ? 'Write “what this image shows” and pick a scene first'
                                                : 'Rewrites the full prompt from “what this image shows” + the scene — no image, free'}
                                            onClick={() => writePromptOnly(selected, draft.description, draft.scene)}
                                        >
                                            {busy[`prm${selected.id}`] ? 'Rewriting…' : '↻ Regenerate prompt from “what this image shows”'}
                                        </button>
                                    </div>
                                    <textarea
                                        className="form-control mb-1"
                                        rows={8}
                                        value={draft.prompt}
                                        onChange={(e) => setDraft({ ...draft, prompt: e.target.value })}
                                        placeholder="The detailed prompt. Rebuild it from your description above, or edit it directly — “Regenerate image” uses whatever is here."
                                    />
                                    <div className="form-text">“Regenerate image” uses this prompt. Edit it directly, or rebuild it from your short description with the button above.</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* AUDIO */}
                    <div className="col-lg-6">
                        <div className="card h-100">
                            <div className="card-header d-flex align-items-center">
                                Voiceover
                                <span className={`ve-chip ms-2 ${selAud.state}`}>{selAud.state}</span>
                                {selAud.state === 'failed' && selected.audio_error && (
                                    <span className="text-danger small ms-2 text-truncate" title={selected.audio_error}>{selected.audio_error}</span>
                                )}
                                {selAud.state === 'generating' && stuckMinutes > 2 && (
                                    <button className="btn btn-link btn-sm p-0 ms-2 text-warning-emphasis"
                                        onClick={() => regenerateAudio(selected, draft.voiceText, draft.voice)}>
                                        Taking long? Cancel & retry
                                    </button>
                                )}
                            </div>
                            <div className="card-body">
                                <label className="form-label">Voiceover text</label>
                                <textarea
                                    className="form-control mb-2"
                                    rows={4}
                                    value={draft.voiceText}
                                    onChange={(e) => setDraft({ ...draft, voiceText: e.target.value })}
                                    onBlur={() => saveSectionField(selected, { voice_text: draft.voiceText, scene_number: selected.scene_number })}
                                />
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <label className="form-label mb-0">Voice</label>
                                    <select className="form-select form-select-sm w-auto" value={draft.voice}
                                        onChange={(e) => setDraft({ ...draft, voice: e.target.value })}>
                                        {VOICES.map((v) => <option key={v} value={v}>{v[0].toUpperCase() + v.slice(1)}</option>)}
                                    </select>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        disabled={busy[`aud${selected.id}`] || selAud.state === 'generating' || !draft.voiceText}
                                        onClick={() => regenerateAudio(selected, draft.voiceText, draft.voice)}
                                    >
                                        {selAud.state === 'generating' ? 'Generating…'
                                            : `${selected.voice?.temporary_url ? 'Regenerate' : 'Generate'} audio · 1 cr`}
                                    </button>
                                </div>
                                {selected.voice?.temporary_url
                                    ? <audio controls src={selected.voice.temporary_url} className="w-100" />
                                    : <p className="text-muted mb-0">No audio yet — write the voiceover and generate it (1 credit).</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Cast drawer ──────────────────────────────────────── */}
            {drawerOpen && (
                <>
                    <div className="ve-drawer-backdrop" onClick={() => setDrawerOpen(false)} />
                    <div className="ve-drawer">
                        <div className="d-flex align-items-center mb-3">
                            <h5 className="mb-0">Cast</h5>
                            <button className="btn-close ms-auto" onClick={() => setDrawerOpen(false)} />
                        </div>
                        <p className="text-muted small">
                            Reference images keep each character looking the same in every section.
                            Changes here affect all sections that use the character.
                        </p>

                        {characters.map((c) => (
                            <div key={c.id} className="border rounded p-2 mb-3">
                                <div className="d-flex gap-2">
                                    {c.tempUrl
                                        ? <img src={c.tempUrl} alt={c.name} className="ve-cast-img" />
                                        : (
                                            <div className="ve-cast-placeholder">
                                                {charBusy[c.id] ? 'generating…' : 'no reference yet'}
                                            </div>
                                        )}
                                    <div className="flex-grow-1">
                                        <strong>{c.name}</strong>
                                        <div className="text-muted small">used in {usedInCount(c.id)} section{usedInCount(c.id) === 1 ? '' : 's'}</div>
                                        {c.image_error && <div className="text-danger small">{c.image_error}</div>}
                                    </div>
                                </div>
                                <CastDescription character={c} onSave={saveDescription} />
                                <button
                                    className="btn btn-sm btn-primary mt-1"
                                    disabled={charBusy[c.id]}
                                    onClick={() => regenerateCharacter(c)}
                                >
                                    {charBusy[c.id] ? 'Generating…' : `${c.asset_id ? 'Regenerate' : 'Generate'} reference · 1 cr`}
                                </button>
                            </div>
                        ))}

                        {overallLook && (
                            <div className="border rounded p-2">
                                <strong>Overall look</strong>
                                <div className="text-muted small mb-1">Applies to every image in this video.</div>
                                <CastDescription character={overallLook} onSave={saveDescription} rows={4} />
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ── Watch modal ──────────────────────────────────────── */}
            {watchOpen && videoUrl && (
                <div className="ve-modal-backdrop" onClick={() => setWatchOpen(false)}>
                    <div className="ve-modal ve-modal-video" onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex align-items-center p-2 px-3 border-bottom">
                            <h6 className="mb-0">{video.title}</h6>
                            <button className="btn-close ms-auto" onClick={() => setWatchOpen(false)} />
                        </div>
                        <div className="p-2">
                            <video controls autoPlay src={videoUrl} className="w-100 rounded" />
                        </div>
                        <div className="d-flex gap-2 px-3 pb-3">
                            <a className="btn btn-sm btn-outline-secondary" href={`/videos/${video.uuid}/download`} target="_blank" rel="noopener noreferrer">Download</a>
                            <button className="btn btn-sm btn-outline-secondary" onClick={setPrimary} title="The main video is the one shown on your script's analysis page">
                                Use as main video
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Scene text modal ─────────────────────────────────── */}
            {sceneModal && (
                <div className="ve-modal-backdrop" onClick={() => setSceneModal(null)}>
                    <div className="ve-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex align-items-center p-3 border-bottom">
                            <h6 className="mb-0">Scene {sceneModal}{sceneMap[sceneModal] ? ` — ${sceneMap[sceneModal]}` : ''}</h6>
                            <button className="btn-close ms-auto" onClick={() => setSceneModal(null)} />
                        </div>
                        <div className="ve-modal-body">{sceneTextMap[sceneModal] || 'No text available for this scene.'}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Description textarea with save-on-change affordance (cast + overall look).
function CastDescription({ character, onSave, rows = 3 }) {
    const [text, setText] = useState(character.description || '');
    const [dirty, setDirty] = useState(false);
    useEffect(() => { setText(character.description || ''); setDirty(false); }, [character.id, character.description]);
    return (
        <div className="mt-2">
            <textarea
                className="form-control form-control-sm"
                rows={rows}
                value={text}
                onChange={(e) => { setText(e.target.value); setDirty(true); }}
            />
            {dirty && (
                <button className="btn btn-sm btn-success mt-1 me-2" onClick={() => { onSave(character, text); setDirty(false); }}>
                    Save
                </button>
            )}
        </div>
    );
}
